/**
 * The one module encoding conversation scope precedence.
 *
 * A conversation list — and a conversation opened from it — lives in exactly
 * one scope. When several scope descriptors are present at once, the winner
 * is decided here and nowhere else:
 *
 *   tickets > chats queue > inbox > team > custom view (folder) >
 *   conversation type > default
 *
 * Consumers stay thin: `conversationUrl` and `conversationListPageURL` in
 * `URLHelper.js` map their legacy argument names onto this module, and
 * `ChatList.redirectToConversationList` derives its conversation type via
 * `conversationTypeForRouteName`.
 */

// One row per conversation-type view: the type token used across props and
// stores, the path segment its URLs live under, and the route names (list
// page + conversation page) that mark the view.
const CONVERSATION_TYPE_VIEWS = [
  {
    conversationType: 'mention',
    path: 'mentions/conversations',
    routeNames: ['conversation_mentions', 'conversation_through_mentions'],
  },
  {
    conversationType: 'participating',
    path: 'participating/conversations',
    routeNames: [
      'conversation_participating',
      'conversation_through_participating',
    ],
  },
  {
    conversationType: 'unattended',
    path: 'unattended/conversations',
    routeNames: ['conversation_unattended', 'conversation_through_unattended'],
  },
  {
    conversationType: 'ai_handling',
    path: 'ai-handling/conversations',
    routeNames: [
      'conversation_ai_handling',
      'conversation_through_ai_handling',
    ],
  },
  {
    conversationType: 'resolved_by_ai',
    path: 'ai-resolved/conversations',
    routeNames: [
      'conversation_resolved_by_ai',
      'conversation_through_resolved_by_ai',
    ],
  },
];

const conversationTypeView = conversationType =>
  CONVERSATION_TYPE_VIEWS.find(
    view => view.conversationType === conversationType
  );

/**
 * Resolves the winning scope from the descriptors present. This ladder IS
 * the precedence — nothing else may re-encode it.
 *
 * @param {Object} descriptors
 * @param {boolean} [descriptors.tickets] the tickets screen
 * @param {string} [descriptors.queueSlug] a Chats queue, e.g. `for-you`
 * @param {string|number} [descriptors.inboxId] an inbox
 * @param {string|number} [descriptors.teamId] a team inbox
 * @param {string|number} [descriptors.customViewId] a custom view (folder)
 * @param {string} [descriptors.conversationType] a conversation-type view
 * @returns {Object} one of `{ type: 'tickets' }`, `{ type: 'queue', queueSlug }`,
 *   `{ type: 'inbox', inboxId }`, `{ type: 'team', teamId }`,
 *   `{ type: 'customView', customViewId }`,
 *   `{ type: 'conversationType', conversationType }`, `{ type: 'default' }`
 */
export const resolveConversationScope = ({
  tickets = false,
  queueSlug = '',
  inboxId,
  teamId,
  customViewId,
  conversationType = '',
} = {}) => {
  // The tickets screen owns its own scoping in the query string (`view`,
  // `custom_view`), so none of the path-based scopes below apply to it.
  if (tickets) return { type: 'tickets' };
  // A Chats queue keeps the conversation inside its own path so the queue
  // stays the context — and the sidebar entry stays lit — while it is open.
  if (queueSlug) return { type: 'queue', queueSlug };
  if (inboxId) return { type: 'inbox', inboxId };
  // A team inbox scopes the same list to one team's conversations; like an
  // inbox, the open conversation stays inside the team's path.
  if (teamId) return { type: 'team', teamId };
  if (customViewId) return { type: 'customView', customViewId };
  if (conversationType) return { type: 'conversationType', conversationType };
  return { type: 'default' };
};

/**
 * The list page path for a scope, relative to the frontend root (no `/app/`
 * prefix — `frontendURL` adds that).
 */
export const conversationListPath = ({ accountId, ...descriptors }) => {
  const scope = resolveConversationScope(descriptors);
  switch (scope.type) {
    case 'tickets':
      return `accounts/${accountId}/tickets`;
    case 'queue':
      return `accounts/${accountId}/${scope.queueSlug}/conversations`;
    case 'inbox':
      return `accounts/${accountId}/inbox/${scope.inboxId}`;
    case 'team':
      return `accounts/${accountId}/team/${scope.teamId}`;
    case 'customView':
      return `accounts/${accountId}/custom_view/${scope.customViewId}`;
    case 'conversationType': {
      // An unknown type interpolates as `undefined`, exactly as the historical
      // `urlMap[conversationType]` lookup did; callers only pass known types.
      const view = conversationTypeView(scope.conversationType);
      return `accounts/${accountId}/${view?.path}`;
    }
    default:
      return `accounts/${accountId}/dashboard`;
  }
};

/**
 * The path of a conversation opened inside a scope, relative to the frontend
 * root (no `/app/` prefix — `frontendURL` adds that).
 */
export const conversationPath = ({
  accountId,
  conversationId,
  ...descriptors
}) => {
  const scope = resolveConversationScope(descriptors);
  switch (scope.type) {
    case 'tickets':
      return `accounts/${accountId}/tickets/${conversationId}`;
    case 'queue':
      return `accounts/${accountId}/${scope.queueSlug}/conversations/${conversationId}`;
    case 'inbox':
      return `accounts/${accountId}/inbox/${scope.inboxId}/conversations/${conversationId}`;
    case 'team':
      return `accounts/${accountId}/team/${scope.teamId}/conversations/${conversationId}`;
    case 'customView':
      return `accounts/${accountId}/custom_view/${scope.customViewId}/conversations/${conversationId}`;
    case 'conversationType': {
      const view = conversationTypeView(scope.conversationType);
      // An unknown type falls back to the plain conversation path, exactly as
      // the historical if/else ladder did.
      if (!view) return `accounts/${accountId}/conversations/${conversationId}`;
      return `accounts/${accountId}/${view.path}/${conversationId}`;
    }
    default:
      return `accounts/${accountId}/conversations/${conversationId}`;
  }
};

/**
 * The conversation type marked by a route name, for redirects that must stay
 * inside the view the agent is on. Returns '' when the route is not a
 * conversation-type view.
 */
export const conversationTypeForRouteName = routeName =>
  CONVERSATION_TYPE_VIEWS.find(view => view.routeNames.includes(routeName))
    ?.conversationType ?? '';
